/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState} from 'react'
import {useCognitoAuth} from '../../../../app/modules/auth'
import {toAbsoluteUrl} from '@/shared/utils'
import {Link} from 'react-router-dom'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {useTheme} from '@/shared/theme/ThemeContext'

const HeaderUserMenu: FC = () => {
  const {currentUser, cognitoLogout} = useCognitoAuth()
  const {theme, toggleTheme} = useTheme()
  const [imageError, setImageError] = useState(false)
  const [logoutLoading, setLogoutLoading] = useState(false)

  // Get profile photo URL - use Google profile picture if available, otherwise default avatar
  const profilePhotoUrl =
    currentUser?.picture && !imageError
      ? currentUser.picture
      : toAbsoluteUrl('/media/avatars/blank.png')

  const handleImageError = () => {
    setImageError(true)
  }

  const handleLogout = async () => {
    setLogoutLoading(true)
    try {
      // Use the proper cognitoLogout function that handles cleanup and redirect
      await cognitoLogout()
      // No need to navigate manually - cognitoLogout handles the redirect
    } catch (error) {
      console.error('Error signing out:', error)
      toast.error(
        `Failed to sign out: ${error instanceof Error ? error.message : 'Unknown error'}`,
        toastDefaultConfig
      )
      getLogger().error('Failed to sign out:', error)
      setLogoutLoading(false)
    }
  }

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold py-4 fs-6 w-275px'
      data-kt-menu='true'
    >
      <div className='menu-item px-3'>
        <div className='menu-content d-flex align-items-center px-3'>
          <div className='symbol symbol-50px me-5'>
            <img alt='Profile' src={profilePhotoUrl} onError={handleImageError} />
          </div>

          <div className='d-flex flex-column'>
            <div className='fw-bolder d-flex align-items-center fs-5'>
              {currentUser?.name
                ? `${currentUser.name}${currentUser.familyName ? ` ${currentUser.familyName}` : ''}`
                : 'Anonymous User'}
            </div>
            <div className='fw-bold text-muted text-hover-primary fs-7'>
              {currentUser?.email ? currentUser.email : 'No email'}
            </div>
          </div>
        </div>
      </div>

      <div className='separator my-2'></div>

      <div className='menu-item px-5'>
        <Link to='/admin' className='menu-link px-5'>
          <i className='fas fa-cog me-2 text-gray-500' style={{width: '1.25em', textAlign: 'center'}} />
          Settings
        </Link>
      </div>
      <div className='menu-item px-5'>
        <a
          href='http://account.biobam.com'
          target='_blank'
          rel='noreferrer'
          className='menu-link px-5'
        >
          <i className='fas fa-user-circle me-2 text-gray-500' style={{width: '1.25em', textAlign: 'center'}} />
          BioBam Account
        </a>
      </div>
      <div className='menu-item px-5'>
        <a role='button' onClick={toggleTheme} className='menu-link px-5'>
          <i
            className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} me-2 text-gray-500`}
            style={{width: '1.25em', textAlign: 'center'}}
          />
          {theme === 'dark' ? 'Light mode' : 'Dark mode'}
        </a>
      </div>
      {/*
        The OmicsAI Agent entry used to sit here. It is a workspace panel, not an account setting,
        so it moved to the header's layout cluster (LayoutPanelToggles) together with its
        internal-testers gate.
      */}
      <div className='separator my-2'></div>

      <div className='menu-item px-5'>
        <a
          role='button'
          onClick={!logoutLoading ? handleLogout : undefined}
          className={`menu-link px-5 text-danger${logoutLoading ? ' pe-none opacity-50' : ''}`}
        >
          {logoutLoading ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
                style={{width: '1.25em', textAlign: 'center'} as React.CSSProperties}
              />
              Signing out...
            </>
          ) : (
            <>
              <i className='fas fa-sign-out-alt me-2' style={{width: '1.25em', textAlign: 'center'}} />
              Sign Out
            </>
          )}
        </a>
      </div>
    </div>
  )
}

export {HeaderUserMenu}
